import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import ToggleButton from "@mui/material/ToggleButton";
import { SFGridContainer, SFGridItem, SFStack } from "../grid";
import { SFCheckIcon } from "../icons";
import { SFUseStyled } from "@sf-core";

const { styled } = SFUseStyled();

const StyledDiv = styled("div", {
  shouldForwardProp: (prop) => prop !== "maxLabelWidth",
})(({ theme, maxLabelWidth }) => ({
  maxWidth: maxLabelWidth,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

export const SFToggleButton = ({
  value,
  label,
  handleChange,
  selected = false,
  size,
  maxLabelWidth,
  variant = "default",
  noHover = false,
  disabled = false,
}) => {
  return (
    <ToggleButton
      size={size}
      variant={selected ? "selected" : variant}
      value={value}
      aria-label={value}
      onChange={handleChange}
      noHover={noHover}
      disabled={disabled}
    >
      <SFStack direction="row" spacing={1} alignItems={"center"}>
        {selected && <SFCheckIcon color="blue300" fontSize={"small"} />}
        <StyledDiv maxLabelWidth={maxLabelWidth}>{label}</StyledDiv>
      </SFStack>
    </ToggleButton>
  );
};

const SFToggleButtons = ({
  items,
  selectedItems,
  onChange,
  variant = "default",
  size = "medium",
  haveCollapsibleItems = false,
  spacing = 1.5,
  disabled = false,
  limit = 0,
  noHover = false,
}) => {
  const [selected, setSelected] = useState(selectedItems ?? []);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [visibleItems, setVisibleItems] = useState([]);
  const [collapsedItemsCount, setCollapsedItemsCount] = useState(0);

  const handleChange = (event, value) => {
    setSelected((prv) => {
      let newSelection = prv.slice();
      if (newSelection.includes(value)) {
        return newSelection.filter((x) => x !== value);
      } else {
        newSelection.push(value);
        return newSelection;
      }
    });
  };

  useEffect(() => {
    onChange && onChange(selected);
  }, [selected.length]);

  const containerRef = useRef(null);

  useEffect(() => {
    // Calculate visible items and collapsed items count when limit is set
    if (haveCollapsibleItems && limit > 0) {
      limit < items.length
        ? setVisibleItems(items.slice(0, limit))
        : setVisibleItems(items);
    }
    const handleResize = () => {
      // Calculate available width for toggle buttons every time window is resized
      setAvailableWidth(containerRef.current.offsetWidth - 60);
    };
    handleResize();
     
    window.addEventListener("resize", handleResize);
    return () => {
       
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    // Calculate visible items every time width changes and there is no limit
    if (limit == 0)
      haveCollapsibleItems
        ? setVisibleItems(calculateVisibleItems(items))
        : setVisibleItems(items);
  }, [availableWidth]);

  useEffect(() => {
    // Calculate collapsed items count every time visible items change
    if (haveCollapsibleItems)
      setCollapsedItemsCount(items.length - visibleItems.length);
  }, [visibleItems]);

  const calculateVisibleItems = (allItems) => {
    const extraPadding = 30; // Padding between toggle buttons
    let itemsWithFirst = allItems.slice(); // Copy array
    let firstItem = itemsWithFirst.shift(); // Remove first item from array

    let maxLabelWidthForFirstItem = availableWidth - extraPadding; // Calculate max width for first item
    let remainingWidth =
      maxLabelWidthForFirstItem - firstItem.label?.toString().length * 5.5; // Calculate remaining width for other items
    firstItem = { ...firstItem, maxLabelWidth: maxLabelWidthForFirstItem };

    // Filter visible items based on if they fit in the remaining width
    let otherItems = itemsWithFirst.filter((item) => {
      let widthRequired = extraPadding + item.label?.toString().length * 5.5;
      if (remainingWidth - widthRequired >= 0) {
        remainingWidth -= widthRequired;
        return true;
      }
      return false;
    });

    // Return always the first item plus all other items that fit in available width
    return [firstItem, ...otherItems];
  };

  return (
    <div ref={containerRef}>
      <SFGridContainer spacing={spacing}>
        {visibleItems.map((item, index) => (
          <SFGridItem key={index}>
            <SFToggleButton
              size={size}
              value={item.value}
              label={item.label}
              handleChange={disabled ? () => { } : handleChange}
              selected={selected.includes(item.value)}
              maxLabelWidth={item.maxLabelWidth}
              variant={variant}
              noHover={noHover}
              disabled={disabled}
            />
          </SFGridItem>
        ))}
        {collapsedItemsCount > 0 && (
          <SFGridItem key={"collapsedItemsCount"}>
            <SFToggleButton
              size={size}
              variant={variant}
              value={"collapsedItemsCount"}
              label={`+${collapsedItemsCount}`}
              handleChange={() => {
                if (limit > 0) {
                  setCollapsedItemsCount(null);
                  setVisibleItems(items);
                }
              }}
              noHover={false}
              disabled={disabled}
            />
          </SFGridItem>
        )}
      </SFGridContainer>
    </div>
  );
};

SFToggleButtons.propTypes = {
  size: PropTypes.oneOf(["small", "medium", "large"]),
  disabled: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  //default selected items
  selectedItems: PropTypes.array,
  onChange: PropTypes.func,
  haveCollapsibleItems: PropTypes.bool,
};

export default SFToggleButtons;
